import React, { Component } from "react";
import { withRouter } from "react-router-dom";
import moment from "moment";
import FieldModal from "../../../components/modals/fieldModal";
import InputField from "../../../components/inputs/inputField";
import ViewsTable from "../../../components/tables/ViewsTable";
import LoadingWizard from "../../../components/spinner/wizard";
import XLSX from "xlsx";
import { saveAs } from "file-saver";

// Utility function to normalize errors into a consistent array format
const normalizeErrorList = (e) => {
	if (Array.isArray(e)) {
		return e;
	} else if (e?.error && Array.isArray(e.error)) {
		return e.error;
	} else if (e?.error) {
		return [e.error];
	} else {
		return [e];
	}
};

class FormSubmissions extends Component {
	constructor(props) {
		super(props);

		// Process initial column data
		const { columns, columnTypes, toggledColumns, normalizedColumns } = this.processBaseColumns();

		// Process form field data
		const { toggledFieldLabels, normalizedFieldLabels } = this.processFormFields(
			props.form,
			toggledColumns,
			normalizedColumns
		);

		// Process custom fields
		this.processCustomFields(
			props.event,
			columns,
			columnTypes,
			toggledColumns,
			normalizedColumns
		);

		this.state = {
			loading: false,
			columns,
			columnTypes,
			normalizedColumns,
			toggledColumns,
			normalizedFieldLabels,
			toggledFieldLabels,
			checkedSubmission: {},
			submissionDetailsModalTrigger: false,
			selectedColumnSort: null,
			sortDesc: false,
			searchTerm: "",
			searching: false,
			// Cache processed submission data to avoid recalculation on each render
			processedSubmissions: null,
			filteredSubmissions: null,
		};

		// For search debouncing
		this.searchTimeout = null;
	}

	// Break down heavy constructor into manageable pieces
	processBaseColumns = () => {
		const columns = {
			email: "Email",
			firstName: "First Name",
			lastName: "Last Name",
			submittedAt: "Submitted",
			errors: "Errors"
		};

		const columnTypes = {
			email: "text",
			firstName: "text",
			lastName: "text",
			submittedAt: "timestamp",
			errors: "text"
		};

		if (this.props.containsTickets) {
			columns.ticket = "Ticket";
			columnTypes.ticket = "text";
		}

		let containsSession = false;
		this.props.form.submissions.forEach((submission) => {
			if (submission.session) {
				containsSession = true;
				return;
			}
		});
		if (containsSession) {
			columns.sessionName = "Session";
			columnTypes.sessionName = "text";
		}

		const toggledColumns = {};
		const normalizedColumns = Object.keys(columns).map((cfc) => {
			toggledColumns[cfc] = true;
			return {
				label: columns[cfc],
				key: cfc,
				value: cfc,
				type: columnTypes[cfc],
				sortAsc: false,
				sortDesc: false,
			};
		});

		return { columns, columnTypes, toggledColumns, normalizedColumns };
	}

	processFormFields = (form, toggledColumns, normalizedColumns) => {
		const toggledFieldLabels = { ...toggledColumns };
		const normalizedFieldLabels = [...normalizedColumns];

		form.pages.forEach((page) => {
			page.sections.forEach((section) => {
				section.fields.forEach((field) => {
					toggledFieldLabels[field.id] = true;
					normalizedFieldLabels.push({
						key: field.id,
						label: field.label,
						type: field.type,
						value: field.id,
						sortAsc: false,
						sortDesc: false,
					});

					if (field.type === "nestedSections" || field.type === "groupSelection") {
						field.nestedSections.forEach((nestedSection) => {
							nestedSection.questions.forEach((question) => {
								toggledFieldLabels[question.id] = true;
								normalizedFieldLabels.push({
									key: question.id,
									label: question.label,
									type: question.type,
									value: question.id,
									sortAsc: false,
									sortDesc: false,
								});
							});
						});
					}
				});
			});
		});

		return { toggledFieldLabels, normalizedFieldLabels };
	}

	processCustomFields = (event, columns, columnTypes, toggledColumns, normalizedColumns) => {
		event.customFields.attendees.forEach((cfcategory) => {
			cfcategory.fields.forEach((field) => {
				columns[field._id] = field.fieldName;
				columnTypes[field._id] = field.fieldType;
				toggledColumns[field._id] = true;

				normalizedColumns.push({
					label: field.fieldName,
					key: field._id,
					value: field._id,
					type: field.fieldType,
					sortAsc: false,
					sortDesc: false,
				});
			});
		});
	}

	// Add component lifecycle methods to handle data processing
	componentDidMount() {
		// Process submissions once on component mount
		this.processSubmissionsData();
	}

	componentDidUpdate(prevProps) {
		// Only reprocess data when submission-related props change
		if (prevProps.editedForm.submissions !== this.props.editedForm.submissions) {
			this.processSubmissionsData();
		}
	}

	// Normalize submissions to ensure consistent extraFields structure
	normalizeSubmissions = (submissions) => {
		if (!submissions?.length) return;
		const lastSub = submissions[submissions.length - 1];
		let masterFields = {};
		try {
			masterFields = JSON.parse(lastSub.formData && lastSub.formData.extraFields || "{}");
		} catch (e) {
			masterFields = {};
		}
		const masterKeys = Object.keys(masterFields);
		submissions.forEach((sub) => {
			let currentFields = {};
			try {
				currentFields = JSON.parse(sub.formData && sub.formData.extraFields || "{}");
			} catch (e) {
				currentFields = {};
			}
			
			const normalizedFields = {};
			
			masterKeys.forEach((key) => {
				if (key in currentFields) {
					normalizedFields[key] = currentFields[key];
				} else {
					normalizedFields[key] = null;
				}
			});
			
			if (!sub.formData) sub.formData = {};
			sub.formData.extraFields = JSON.stringify(normalizedFields);
		});
	};

	// Process submissions data once and cache it
	processSubmissionsData = () => {
		const { editedForm } = this.props;
		// Normalize submissions before processing
		this.normalizeSubmissions(editedForm.submissions);

		const processedSubmissions = editedForm.submissions.map((a) => {
	
			if (a.formData['redeemedInventory']) {
				const cleanValue = a.formData['redeemedInventory'].replace(/\n/g, ',')
				a.formData['redeemedInventory'] = cleanValue;
			}
			if (a.formData.roomingInformation && typeof a.formData.roomingInformation === "string") {
				a.formData.roomingInformation = JSON.parse(a.formData.roomingInformation);
			}

			if (a.formData.travelInformation && typeof a.formData.travelInformation === "string") {
				a.formData.travelInformation = JSON.parse(a.formData.travelInformation);
			}
			let extraFields =
				typeof a.formData.extraFields == "string"
					? JSON.parse(a.formData.extraFields)
					: a.formData.extraFields;

			let additionalAttendees = [];
			if (a.formData.additionalAttendees) {
				try {
					additionalAttendees = typeof a.formData.additionalAttendees === "string"
						? JSON.parse(a.formData.additionalAttendees)
						: a.formData.additionalAttendees;

					if (!Array.isArray(additionalAttendees)) {
						additionalAttendees = [];
					}
				} catch (e) {
					additionalAttendees = [];
				}
			}
			a.formData.additionalAttendees = additionalAttendees.map(att => Object.values(att).join(", ")).join("; ");

			let salesforceErrors = "";
			if (a.formData.errors != null && a.formData.errors.length) {
				salesforceErrors = a.formData.errors
					.map((e) => {
						const errList = normalizeErrorList(e);

						return errList
							.map((ed) => {
								if (!ed) return "";
								if (typeof ed === "object") {
									return `${ed.message || "Error"}${ed.errorCode ? ` (${ed.errorCode})` : ""}`;
								}
								return `${ed}`;
							})
							.join(", ");
					})
					.join("; ");
			}

			const rooming = Object.keys(a.formData.roomingInformation || {}).reduce((acc, key) => {
				acc[`roomingInformation-${key}`] = a.formData.roomingInformation[key];
				return acc;
			}, {});

			const travel = Object.keys(a.formData.travelInformation || {}).reduce((acc, key) => {
				acc[`travelInformation-${key}`] = a.formData.travelInformation[key];
				return acc;
			}, {});

			return {
				_id: a._id,
				firstName:
					a.attendee.firstName == "-"
						? a.formData.firstName || "-"
						: a.attendee.firstName,
				lastName:
					a.attendee.lastName == "-"
						? a.formData.lastName || "-"
						: a.attendee.lastName,
				email:
					a.attendee.email == "-" ? a.formData.email || "-" : a.attendee.email,
				submittedAt: a.submittedAt,
				sessionName: a.session ? a.session.name : "-",
				ticket: a.ticket ? a.ticket.name : "-",
				...extraFields,
				...a.formData,

				...rooming,
				...travel,

				errors: salesforceErrors,

			};
		});

		const hasErrors = processedSubmissions.some(s => s.errors != null && s.errors !== "");

		this.setState((prevState) => ({
			processedSubmissions,
			filteredSubmissions: processedSubmissions,
			toggledFieldLabels: {
				...prevState.toggledFieldLabels,
				errors: hasErrors,
			},
		}), () => {
			this.applySorting();
		});
	}

	// Apply sorting to cached submissions
	applySorting = () => {
		const { selectedColumnSort, sortDesc, processedSubmissions } = this.state;

		if (!selectedColumnSort || !processedSubmissions) return;

		const sortedData = [...processedSubmissions].sort((a, b) => {
			const columnType =
				selectedColumnSort.type || typeof a[selectedColumnSort.key];
			if (a[selectedColumnSort.key] && b[selectedColumnSort.key]) {
				switch (columnType) {
					case "string":
						return a[selectedColumnSort.key]
							.toLowerCase()
							.trim()
							.localeCompare(b[selectedColumnSort.key].toLowerCase().trim());
					case "number":
						return a[selectedColumnSort.key] - b[selectedColumnSort.key];
					case "date":
					case "time":
						return (
							new Date(a[selectedColumnSort.key]).getTime() -
							new Date(b[selectedColumnSort.key]).getTime()
						);
					case "boolean":
						const a1 = a[selectedColumnSort.key] ? "yes" : "no";
						const b1 = b[selectedColumnSort.key] ? "yes" : "no";
						return a1.localeCompare(b1);
					default:
						return a[selectedColumnSort.key]
							.toLowerCase()
							.trim()
							.localeCompare(b[selectedColumnSort.key].toLowerCase().trim());
				}
			} else if (a[selectedColumnSort.key]) {
				return 1;
			}
			return -1;
		});

		const finalSorted = sortDesc ? sortedData.reverse() : sortedData;

		// Apply search filter to the sorted data
		this.applySearchFilter(finalSorted);
	}

	// Apply search filter to submissions
	applySearchFilter = (data = null) => {
		const { searchTerm, columns, processedSubmissions } = this.state;
		const dataToFilter = data || processedSubmissions;

		if (!dataToFilter) return;

		if (searchTerm.length === 0) {
			this.setState({ filteredSubmissions: dataToFilter });
			return;
		}

		let filteredData = [...dataToFilter];
		const searchTerms = searchTerm.toLowerCase().split(" ");

		searchTerms.forEach((term) => {
			if (term !== "") {
				filteredData = filteredData.filter((entry) => {
					let found = false;
					Object.keys(columns).forEach((key) => {
						if (
							entry[key] &&
							entry[key].toString().toLowerCase().includes(term)
						) {
							found = true;
						}
					});
					return found;
				});
			}
		});

		this.setState({ filteredSubmissions: filteredData });
	}

	updatedCheckedSubmissions = (checked) => {
		this.setState({ checkedSubmission: checked });
	};

	toggle = (id) => {
		this.setState({
			submissionDetailsModalTrigger: !this.state.submissionDetailsModalTrigger,
			selectedSubmissionId: id || "",
		});
	};

	s2ab = (s) => {
		var buf = new ArrayBuffer(s.length);
		var view = new Uint8Array(buf);
		for (var i = 0; i < s.length; i++) view[i] = s.charCodeAt(i) & 0xff;
		return buf;
	};

	download = () => {
		const { event, editedForm } = this.props;
		const { normalizedFieldLabels, filteredSubmissions } = this.state;

		let headers = ["First Name", "Last Name", "Email", "Submitted On"];
		const columns = normalizedFieldLabels.reduce((acc, cur) => {
			acc[cur.key] = cur.label;
			return acc;
		}, {});

		Object.keys(columns).forEach((key) => {
			headers.push(columns[key]);
		});

		const rows = filteredSubmissions.map(sub => {
			let fixedSub = [
				sub.firstName,
				sub.lastName,
				sub.email,
				moment(sub.submittedAt).format("MM/DD/YYYY h:mm a")
			];

			Object.keys(columns).forEach((key) => {
				fixedSub.push(sub[key] || sub[key] === "" ? sub[key] : "");
			});

			return fixedSub;
		});

		var wb = XLSX.utils.book_new();
		wb.Props = {
			Title: editedForm.name + " Submission Report",
			Subject: "Submissions",
			Author: "Simple Events",
			CreatedDate: new Date(),
		};
		wb.SheetNames.push("Data");

		var ws_data2 = [headers, ...rows];
		var ws2 = XLSX.utils.aoa_to_sheet(ws_data2);
		wb.Sheets["Data"] = ws2;

		var wbout = XLSX.write(wb, { bookType: "xlsx", type: "binary" });

		saveAs(
			new Blob([this.s2ab(wbout)], { type: "application/octet-stream" }),
			`${event.name} - ${editedForm.name} .xlsx`
		);
	};

	sortData = (column, sortAsc, sortDesc) => {
		const { normalizedColumns } = this.state;
		const updatedColumns = normalizedColumns.map((col) => {
			if (col.key == column.key) {
				col.sortAsc = sortAsc;
				col.sortDesc = sortDesc;
			} else {
				col.sortAsc = false;
				col.sortDesc = false;
			}
			return col;
		});

		this.setState({
			normalizedColumns: updatedColumns,
			selectedColumnSort: column,
			sortDesc: sortDesc ? true : false,
		}, () => this.applySorting());
	};

	searchEntries = (searchText) => {
		this.setState({
			searching: searchText !== "",
			searchTerm: searchText || "",
		}, () => this.applySearchFilter());
	};

	clearCheckedSubmissions = () => {
		this.setState({ checkedSubmission: {} });
	};

	handleSearchChange = (e) => {
		// Implement proper debouncing
		if (this.searchTimeout) {
			clearTimeout(this.searchTimeout);
		}

		this.setState({ searchTerm: e.target.value }, () => {
			this.searchTimeout = setTimeout(() => {
				this.searchEntries(this.state.searchTerm);
			}, 300); // Reduced from 1000ms to 300ms for better responsiveness
		});
	};

	clearSearch = () => {
		this.setState({ searchTerm: "", searching: false },
			() => this.searchEntries("")
		);
	};

	handleRowCheck = (entryId) => {
		const checked = { ...this.state.checkedSubmission };

		if (checked[entryId]) {
			delete checked[entryId];
		} else {
			checked[entryId] = true;
		}

		this.setState({ checkedSubmission: checked },
			() => this.updateSubmissionEditing()
		);
	};

	handleAllRowsCheck = (entryIds) => {
		let checked = {};

		if (entryIds.length > 0) {
			entryIds.forEach((entryId) => {
				checked[entryId] = true;
			});
		}

		this.setState({ checkedSubmission: checked },
			() => this.updateSubmissionEditing()
		);
	};

	updateSubmissionEditing = () => {
		const { checkedSubmission } = this.state;
		const hasCheckedItems = Object.keys(checkedSubmission).length > 0;

		if (hasCheckedItems) {
			this.props.toggleSubmissionEditing(
				true,
				checkedSubmission,
				this.clearCheckedSubmissions
			);
		} else {
			this.props.toggleSubmissionEditing(false);
		}
	};

	getCustomFieldColumns = () => {
		const { event } = this.props;
		let customFieldColumns = [];
		let customFieldColumnTypes = [];

		event.customFields.attendees.forEach((category) => {
			category.fields.forEach((f) => {
				customFieldColumns[f._id] = f.fieldName;
				customFieldColumnTypes[f._id] = f.fieldType;
			});
		});

		return { customFieldColumns, customFieldColumnTypes };
	};

	renderSearchInput = () => {
		const { searchTerm } = this.state;
		return (
			<InputField
				classes="m-0 viewSearch"
				placeholder="Search "
				clickIcon={true}
				required={true}
				value={searchTerm}
				onChange={this.handleSearchChange}
				prefix={
					<i
						className="las la-search"
						style={{ marginRight: 5, transform: "rotate(270deg)" }}
					/>
				}
				inputIcon={
					searchTerm !== "" && (
						<i
							onMouseDown={this.clearSearch}
							className="las la-times-circle"
							style={{
								visibility: searchTerm ? "visible" : "hidden",
							}}
						/>
					)
				}
			/>
		);
	};

	render() {
		const {
			loading,
			normalizedFieldLabels,
			toggledFieldLabels,
			checkedSubmission,
			submissionDetailsModalTrigger,
			selectedSubmissionId,
			filteredSubmissions
		} = this.state;

		const { event, editedForm, form, disableCheck = false } = this.props;

		if (loading) {
			return <LoadingWizard text="LOADING ATTENDEE" />;
		}

		// Prevent rendering before data is processed
		if (!filteredSubmissions) {
			return <LoadingWizard text="PROCESSING DATA" />;
		}

		const { customFieldColumns, customFieldColumnTypes } = this.getCustomFieldColumns();


		return (
			<div className="sectionContainer">
				<div style={{ height: "100%" }} className="">
					<div className="actionButtonContainer" style={{ marginBottom: 15 }}>
						<div className="mb-10 mt-10 flex aic">
							{this.renderSearchInput()}
						</div>
						<div className="sectionOptionsContainer">
							<div
								onClick={this.download}
								className="actionButton p-7"
							>
								<div>
									<i className="las la-download" />
								</div>
							</div>
						</div>
					</div>

					<ViewsTable
						title={"Submissions"}
						style={{ margin: 0 }}
						noDescription={true}
						columns={normalizedFieldLabels}
						toggledColumns={toggledFieldLabels}
						data={filteredSubmissions}
						mainColumnActive={false}
						checked={checkedSubmission}
						disableCheck={disableCheck}
						onClick={this.toggle}
						checkRow={this.handleRowCheck}
						checkAllRows={this.handleAllRowsCheck}
						sortData={this.sortData}
					/>

					{submissionDetailsModalTrigger && (
						<SubmissionDetailsModal
							event={event}
							isOpen={submissionDetailsModalTrigger}
							submission={editedForm.submissions.find(
								(s) => s._id === selectedSubmissionId
							)}
							columns={customFieldColumns}
							columnTypes={customFieldColumnTypes}
							toggle={this.toggle}
							form={form}
						/>
					)}
				</div>
			</div>
		);
	}
}

export default withRouter(FormSubmissions);

class SubmissionDetailsModal extends React.Component {
	constructor(props) {
		const columns = props.columns;

		const columnTypes = props.columnTypes;
		const toggledColumns = {};
		const normalizedColumns = Object.keys(columns).map((cfc) => {
			toggledColumns[cfc] = true;
			return {
				label: columns[cfc],
				key: cfc,
				value: cfc,
				type: columnTypes[cfc],
				sortAsc: false,
				sortDesc: false,
			};
		});
		super(props);
		this.state = {
			columns: columns,
			columnTypes: columnTypes,
			normalizedColumns: normalizedColumns,
			toggledColumns: toggledColumns,
			submitting: false,
		};
	}

	render() {
		const { isOpen, submission, form } = this.props;
		const { normalizedColumns, searchTerm } = this.state;
		const entries =
			typeof submission.formData.extraFields == "string"
				? JSON.parse(submission.formData.extraFields)
				: submission.formData.extraFields;
		if (
			submission.formData.roomingInformation &&
			typeof submission.formData.roomingInformation == "string"
		) {
			submission.formData.roomingInformation = JSON.parse(
				submission.formData.roomingInformation
			);
		}
		if (
			submission.formData.travelInformation &&
			typeof submission.formData.travelInformation == "string"
		) {
			submission.formData.travelInformation = JSON.parse(
				submission.formData.travelInformation
			);
		}
		
		return (
			<div>
				<FieldModal
					size="medium"
					isOpen={isOpen}
					bodyDescription={""}
					bodyContent={() => {
						return (
							<div>
								<div className="modalBodySubmission">
									<div className="sectionBlock">
										{form.pages.map((page, pi) => {
											return (
												<div>
													<label className="mb-0">Page {pi + 1}</label>
													<h1 className="mb-10">{page.title}</h1>
													{page.sections.map((section) => {
														return (
															<div>
																<h2
																	className="mb-10"
																	dangerouslySetInnerHTML={{
																		__html: section.title,
																	}}
																></h2>
																{section.fields.map((field) => {
																	let value = "";
																	if (field.objectClass == "baseInput") {
																		if (field.id.includes("-")) {
																			let split = field.id.split("-");
																			const v1 = submission.formData[split[0]];
																			value = v1 ? v1[split[1]] : "";
																		} else {
																			value = submission.formData[field.id];
																		}
																	} else if (
																		field.objectClass == "mappedInput"
																	) {
																		value = entries[field.id];
																	} else {
																		value = entries[field._id];
																	}

																	let eventGroups = this.props.event.groups;
																	let mappedGroups = {};
																	if (eventGroups && eventGroups.length > 0) {
																		eventGroups.forEach((group) => {
																			mappedGroups[group._id] = group.name;
																		});
																	}
																	if (field.type == "groupSelection") {
																		value =
																			mappedGroups[entries[field._id]] || "";
																	}
																	return (
																		<div>
																			<div
																				dangerouslySetInnerHTML={{
																					__html: field.description,
																				}}
																			></div>
																			<div>
																				{field.label}{" "}
																				{normalizedColumns[field._id] && (
																					<span>
																						- {normalizedColumns[field._id]}
																					</span>
																				)}
																			</div>
																			<InputField value={value} disabled={true}>
																				{entries[field._id]}
																			</InputField>
																			{(field.type == "nestedSections" ||
																				field.type == "groupSelection") && (
																					<div>
																						{field.nestedSections.map(
																							(section) => {
																								return (
																									<div className="nestedSectionBlock">
																										<h2
																											className="mb-10"
																											dangerouslySetInnerHTML={{
																												__html: section.title,
																											}}
																										></h2>
																										{section.questions.map(
																											(field) => {
																												let value = "";
																												if (
																													field.objectClass ==
																													"baseInput"
																												) {
																													if (
																														field.id.includes("-")
																													) {
																														let split =
																															field.id.split("-");
																														const v1 =
																															submission.formData[
																															split[0]
																															];
																														value = v1
																															? v1[split[1]]
																															: "";
																													} else {
																														value =
																															submission.formData[
																															field.id
																															];
																													}
																												} else if (
																													field.objectClass ==
																													"mappedInput"
																												) {
																													value =
																														entries[field.id];
																												} else {
																													value =
																														entries[field._id];
																												}
																												return (
																													<div>
																														<div
																															dangerouslySetInnerHTML={{
																																__html:
																																	field.description,
																															}}
																														></div>
																														<div>
																															{field.label}{" "}
																															{normalizedColumns[
																																field._id
																															] && (
																																	<span>
																																		-{" "}
																																		{
																																			normalizedColumns[
																																			field._id
																																			]
																																		}
																																	</span>
																																)}
																														</div>
																														<InputField
																															value={value}
																															disabled={true}
																														>
																															{entries[field._id]}
																														</InputField>
																													</div>
																												);
																											}
																										)}
																									</div>
																								);
																							}
																						)}
																					</div>
																				)}
																		</div>
																	);
																})}
															</div>
														);
													})}
												</div>
											);
										})}
										{(() => {
											const errors = submission.formData.errors;
											if (!errors || !errors.length) return null;
											return (
												<div>
													{errors.map((err, i) => {
												const errList = normalizeErrorList(err);
														return errList.map((ed, j) => {
															if (!ed) return null;

															let errorText = "";
															if (typeof ed === "object") {
																errorText = `${ed.message || "Error"}${ed.errorCode ? ` (${ed.errorCode})` : ""}`;
															} else {
																errorText = `${ed}`;
															}

															return (
																<div key={`${i}-${j}`}>
																	<div>Error</div>
																	<InputField value={errorText} disabled={true} />
																</div>
															);
														});
													})}
												</div>
											);
										})()}
									</div>
								</div>
							</div>
						);
					}}
					toggle={this.props.toggle}
					cancelButtonLabel={"Close"}
				></FieldModal>
			</div>
		);
	}
}
